/**
 * Change against the comparison window (CHG-2026-09-manager-reporting).
 *
 * The distinction that matters is between "no change" and "nothing to compare against". A store
 * that was closed last Sunday has no previous value, and rendering that as ±0 % would claim a
 * measurement nobody took.
 */
export interface Delta {
  kind: 'up' | 'down' | 'flat' | 'unknown'
  /** Fraction, not percent: 0.062 for +6,2 %. Null when there is nothing to compare against. */
  ratio: number | null
}

export function computeDelta(
  current: number,
  previous: number | null | undefined,
): Delta {
  if (previous == null) return { kind: 'unknown', ratio: null }
  if (previous === 0) {
    // Growth from nothing is not a percentage. It is still a direction, and saying so beats
    // printing an infinity or a fabricated 100 %.
    if (current === 0) return { kind: 'flat', ratio: 0 }
    return { kind: 'up', ratio: null }
  }
  const ratio = (current - previous) / previous
  if (Math.abs(ratio) < 0.0005) return { kind: 'flat', ratio: 0 }
  return { kind: ratio > 0 ? 'up' : 'down', ratio }
}

const percentFormatter = new Intl.NumberFormat('de-AT', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: 'exceptZero',
})

// Unsigned, so the "no change" case can wear a ± sign of its own. Built from the same formatter
// family rather than written out, because de-AT puts a non-breaking space before the percent sign
// and a hand-typed literal would differ from every other value in the column by one character.
const unsignedPercentFormatter = new Intl.NumberFormat('de-AT', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: 'never',
})

export function formatDelta(delta: Delta): string | null {
  if (delta.ratio == null) return null
  if (delta.kind === 'flat') return `±${unsignedPercentFormatter.format(0)}`
  return percentFormatter.format(delta.ratio)
}
